import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PortalLayout from '../../components/PortalLayout';

const LARGE_PROPERTY_THRESHOLD_SQM = 5000;
const LARGE_PROPERTY_SURCHARGE = 110;
const TRAVEL_TIER_CUSTOM = 'custom_quote';

export default function PortalBookPackage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('portal_token');
  const clientName = localStorage.getItem('portal_client_name') || '';
  const clientEmail = localStorage.getItem('portal_client_email') || '';

  const [pkg, setPkg] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertySizeSqm, setPropertySizeSqm] = useState('');
  const [notes, setNotes] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState(clientEmail);
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingTime, setBookingTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [travelLoading, setTravelLoading] = useState(false);
  const [travelQuote, setTravelQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const travelDebounceRef = useRef(null);

  useEffect(() => {
    if (!token) { navigate('/portal/login'); return; }
    const load = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/portal/packages`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.status === 401) { navigate('/portal/login'); return; }
        const data = await res.json();
        const found = (data || []).find((p) => p.assignment_id === assignmentId);
        if (!found) { alert('Package not found.'); navigate('/portal/dashboard'); return; }
        setPkg(found);
      } catch (e) { console.error(e); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  // Travel quote debounce
  useEffect(() => {
    setTravelQuote(null);
    if (travelDebounceRef.current) clearTimeout(travelDebounceRef.current);
    const trimmed = (propertyAddress || '').trim();
    if (trimmed.length < 6) { setTravelLoading(false); return; }
    setTravelLoading(true);
    travelDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${backendUrl}/api/bookings/travel-quote?address=${encodeURIComponent(trimmed)}`);
        setTravelQuote(await res.json());
      } catch { setTravelQuote({ success: false, message: 'Could not estimate travel time' }); }
      finally { setTravelLoading(false); }
    }, 800);
    return () => { if (travelDebounceRef.current) clearTimeout(travelDebounceRef.current); };
  }, [propertyAddress, backendUrl]);

  const sizeNum = parseInt(propertySizeSqm, 10);
  const largeSurcharge = !isNaN(sizeNum) && sizeNum > LARGE_PROPERTY_THRESHOLD_SQM ? LARGE_PROPERTY_SURCHARGE : 0;
  const travelFee = travelQuote && travelQuote.success ? Number(travelQuote.travel_fee || 0) : 0;
  const addonsTotal = selectedAddOns.reduce((s, a) => s + Number(a.price || 0), 0);
  const baseTotal = pkg ? Number(pkg.price || 0) + addonsTotal : 0;
  const finalTotal = baseTotal + largeSurcharge + travelFee;
  const blockedByCustomQuote = travelQuote && travelQuote.success && travelQuote.tier === TRAVEL_TIER_CUSTOM;

  const toggleAddon = (a) => {
    setSelectedAddOns((prev) => prev.some((x) => x.name === a.name)
      ? prev.filter((x) => x.name !== a.name)
      : [...prev, a]);
  };

  const loadSlots = async (date) => {
    if (!date || !pkg) return;
    setLoadingSlots(true); setBookingTime('');
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    try {
      const res = await fetch(`${backendUrl}/api/bookings/available-slots?date=${dateStr}&package_name=${encodeURIComponent(pkg.name)}`);
      const data = await res.json();
      setAvailableSlots(data.available_slots || []);
      if (data.available_slots && data.available_slots.length > 0) setBookingTime(data.available_slots[0]);
    } catch (e) { setAvailableSlots([]); }
    finally { setLoadingSlots(false); }
  };

  const handleDateChange = (date) => { setBookingDate(date); loadSlots(date); };

  const submit = async (e) => {
    e.preventDefault();
    if (!bookingDate || !bookingTime) { alert('Select a date and time.'); return; }
    if (availableSlots.length === 0) { alert('No available slots for this date.'); return; }
    if (!policyAccepted) { alert('Please accept the cancellation policy.'); return; }
    if (blockedByCustomQuote) { alert('This property is more than 40 minutes from our base. Please contact us for a custom quote.'); return; }

    setSubmitting(true);
    try {
      const y = bookingDate.getFullYear();
      const m = String(bookingDate.getMonth() + 1).padStart(2, '0');
      const d = String(bookingDate.getDate()).padStart(2, '0');
      const payload = {
        assignment_id: assignmentId,
        property_address: propertyAddress,
        property_size_sqm: sizeNum && !isNaN(sizeNum) ? sizeNum : null,
        booking_date: `${y}-${m}-${d}`,
        booking_time: bookingTime,
        add_ons: selectedAddOns,
        notes,
        policy_accepted: true,
        invoice_email: invoiceEmail || null,
      };
      const res = await fetch(`${backendUrl}/api/portal/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking failed');
      alert('🎉 Booking submitted! Check your email for confirmation.');
      navigate('/portal/dashboard');
    } catch (e) { alert(`Could not book: ${e.message}`); }
    finally { setSubmitting(false); }
  };

  const isNotSunday = (date) => date.getDay() !== 0;

  if (!pkg) return <PortalLayout><div className="text-center py-12 text-gray-500">Loading…</div></PortalLayout>;

  return (
    <PortalLayout>
      <div className="max-w-3xl mx-auto">
        <Link to="/portal/dashboard" className="text-sm text-teal-600 hover:text-teal-700"><i className="ri-arrow-left-line"></i> Back to dashboard</Link>

        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 mt-4">
          <h2 className="text-2xl font-bold text-gray-900">{pkg.name}</h2>
          {pkg.description && <p className="text-gray-600 mt-1">{pkg.description}</p>}
          <div className="flex justify-between items-center mt-4">
            <span className="text-gray-700">{pkg.duration_minutes || 120} min on-site</span>
            <span className="text-2xl font-bold text-teal-600">A${Number(pkg.price).toFixed(2)}</span>
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
          <h3 className="text-lg font-bold text-gray-900">Booking Details</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
              <input type="text" value={clientName} disabled className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
              <input type="email" value={clientEmail} disabled className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send invoice to (optional)</label>
            <input type="email" value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} placeholder="e.g. accounts@yourcompany.com.au" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Address *</label>
            <input required type="text" value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} placeholder="e.g. 123 Kings Rd, Pimlico QLD 4812" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" data-testid="portal-address-input" />
            {travelLoading && <p className="text-xs text-gray-500 mt-1">Estimating travel time…</p>}
            {travelQuote && travelQuote.success && (
              <p className={`text-xs mt-1 ${travelQuote.tier === TRAVEL_TIER_CUSTOM ? 'text-red-600' : travelFee > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {travelQuote.message}
              </p>
            )}
            {travelQuote && !travelQuote.success && travelQuote.message && (
              <p className="text-xs text-gray-500 mt-1">{travelQuote.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Size (sqm)</label>
              <input type="number" min="0" value={propertySizeSqm} onChange={(e) => setPropertySizeSqm(e.target.value)} placeholder="e.g. 4200" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              {largeSurcharge > 0 && <p className="text-xs text-amber-600 mt-1">Large property surcharge +A${largeSurcharge}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes for the crew</label>
              <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate code, contact on-site, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>

          {(pkg.add_ons || []).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Add-ons</label>
              <div className="grid grid-cols-2 gap-2">
                {pkg.add_ons.map((a, i) => {
                  const active = selectedAddOns.some((x) => x.name === a.name);
                  return (
                    <button type="button" key={i} onClick={() => toggleAddon(a)} className={`text-left px-3 py-2 rounded-lg border text-sm transition ${active ? 'bg-teal-50 border-teal-500 text-teal-800' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                      <div className="flex justify-between"><span>{a.name}</span><span className="font-medium">+A${Number(a.price).toFixed(2)}</span></div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date *</label>
              <DatePicker selected={bookingDate} onChange={handleDateChange} minDate={new Date()} filterDate={isNotSunday} placeholderText="Pick a date" className="w-full px-4 py-2 border border-gray-300 rounded-lg" dateFormat="yyyy-MM-dd" />
              <p className="text-xs text-gray-500 mt-1">Sundays unavailable.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
              {loadingSlots ? (
                <div className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500">Loading slots…</div>
              ) : availableSlots.length === 0 ? (
                <div className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-500">{bookingDate ? 'No slots available.' : 'Pick a date first.'}</div>
              ) : (
                <select value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white">
                  {availableSlots.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} className="mt-1 h-4 w-4" data-testid="portal-policy-checkbox" />
              <span className="text-sm text-gray-700">
                I agree to the <span className="font-medium">Cancellation & Reschedule Policy</span>: cancellations or reschedules within 24 hours of the booking incur a $220 (inc GST) late fee. Weather cancellations are waived at admin discretion.
              </span>
            </label>
          </div>

          <div className="border-t border-gray-200 pt-4 space-y-1 text-sm">
            <div className="flex justify-between text-gray-700"><span>Package</span><span>A${Number(pkg.price).toFixed(2)}</span></div>
            {selectedAddOns.length > 0 && selectedAddOns.map((a, i) => (
              <div key={i} className="flex justify-between text-gray-700"><span>{a.name}</span><span>+A${Number(a.price).toFixed(2)}</span></div>
            ))}
            {largeSurcharge > 0 && <div className="flex justify-between text-gray-700"><span>Large property surcharge</span><span>+A${largeSurcharge}</span></div>}
            {travelFee > 0 && <div className="flex justify-between text-gray-700"><span>Travel fee {travelQuote?.duration_minutes ? `(~${Math.round(travelQuote.duration_minutes)} min)` : ''}</span><span>+A${travelFee.toFixed(0)}</span></div>}
            <div className="flex justify-between font-bold text-lg text-gray-900 pt-2"><span>Total</span><span data-testid="portal-booking-total">A${finalTotal.toFixed(2)} incl GST</span></div>
          </div>

          <button type="submit" disabled={submitting || blockedByCustomQuote} data-testid="portal-submit-booking" className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white py-3 rounded-lg font-semibold">
            {submitting ? 'Booking…' : blockedByCustomQuote ? 'Contact us for a quote' : 'Confirm booking'}
          </button>
        </form>
      </div>
    </PortalLayout>
  );
}
